/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect, useContext } from 'react'
import Modal from '../../../../components/Modal'
import { useServiceContext } from '../../../../root'
import { RcmTableDataContext } from '../../../../components/rcm-table/RcmTableDataContext'
import EditForm from './EditForm'
import { getWatermarkById, createWatermark, updateWatermark } from '../../../../services/api/watermarks'
import { toast } from '../../../../services/toast'

interface ModalEditProps {
  watermark?: any
  onClose: () => void
  onSuccess?: () => void
}

const ModalEdit: React.FC<ModalEditProps> = ({ watermark, onClose, onSuccess }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const tableContext = useContext(RcmTableDataContext)

  const [isEdit, setIsEdit] = useState(!!watermark)
  const [isOpen, setIsOpen] = useState(true)
  const [loading, setLoading] = useState<Promise<any> | null>(null)
  const [watermarkData, setWatermarkData] = useState<any>({})

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    if (isEdit && watermark?.id) {
      const promise = getWatermarkById(watermark.id)
      setLoading(promise)

      try {
        const data = await promise
        data.foregroundColor = data?.foregroundColor === null ? '#000000' : data.foregroundColor
        setWatermarkData(data)
      } catch (error) {
        console.error('Error loading watermark:', error)
      } finally {
        setLoading(null)
      }
    }
  }

  const doSave = async (watermarkValues: any) => {
    try {
      let apiCall: Promise<any>

      if (isEdit) {
        const { id, ...dataWithoutId } = watermarkValues
        apiCall = updateWatermark(watermarkValues.id, dataWithoutId)
      } else {
        apiCall = createWatermark(watermarkValues)
      }

      setLoading(apiCall)

      await apiCall

      toast.success(translate(isEdit ? 'WATERMARKS_UPDATE_SUCCESS_MSG' : 'WATERMARKS_CREATE_SUCCESS_MSG'))

      // Refresh the table
      if (tableContext?.refreshTable) {
        tableContext.refreshTable()
      }

      if (onSuccess) {
        onSuccess()
      }

      doClose()
    } catch (error: any) {
      // ApiError has body property, not data
      let errorMsg = translate('ERROR')
      if (error?.body) {
        // Try different error message formats from API
        errorMsg = error.body.message || error.body.error || error.body.errorMessage || JSON.stringify(error.body)
      } else if (error?.message) {
        errorMsg = error.message
      }
      toast.error(errorMsg)
    } finally {
      setLoading(null)
    }
  }

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <Modal
      show={isOpen}
      handleClose={doClose}
      title={translate(isEdit ? 'WATERMARKS_EDIT_WATERMARK' : 'WATERMARKS_ADD_WATERMARK')}
      size="md"
      dataTestId="edit-watermark-dialog"
      className="watermark-modal"
    >
      <EditForm onSubmit={doSave} doClose={doClose} isEdit={isEdit} watermark={watermarkData} loading={loading} />
    </Modal>
  )
}

export default ModalEdit
